import apiClient from './AuthService';
import Pusher, { Channel } from 'pusher-js';
import { getCurrentUser } from './AuthService';

// Interfaces
export interface StartSessionResponse {
  jobId: string;
  wsUrl?: string;
}

export interface ChunkResponse {
  chunkId: string;
  status: string;
}

export interface EndSessionResponse {
  fullTranscript: string;
  totalChunks: number;
  duration?: number;
}

export interface ChunkProcessedEvent {
  type: 'chunk.processed';
  chunkId: string;
  text: string;
  isFinal: boolean;
  timestamp: number;
  chunkNumber?: number;
  duration?: number;
  success?: boolean;
  error?: string;
}

// Service class
class WhisperTranscriptionService {
  private jobId: string | null = null;
  private pusher: Pusher | null = null;
  private channel: Channel | null = null;
  private chunkNumber: number = 0;
  private onChunkProcessed: ((event: ChunkProcessedEvent) => void) | null = null;
  private onError: ((error: string) => void) | null = null;
  private onConnectionChange: ((connected: boolean) => void) | null = null;

  /**
   * Start a new transcription session
   * POST /transcription/realtime/start
   */
  async startSession(): Promise<string> {
    try {
      const response = await apiClient.post<StartSessionResponse>('/transcription/realtime/start', {
        language: 'es'
      });
      this.jobId = response.data.jobId;
      this.chunkNumber = 0; // Reset chunk counter for new session

      console.log('Whisper session started:', this.jobId);
      return this.jobId;
    } catch (error: any) {
      console.error('Error starting Whisper session:', error);
      throw new Error(error.response?.data?.error || 'Failed to start transcription session');
    }
  }

  /**
   * Send an audio chunk for transcription
   * POST /transcription/realtime/chunk
   */
  async sendChunk(audioBlob: Blob): Promise<ChunkResponse> {
    if (!this.jobId) {
      throw new Error('No active session. Call startSession() first.');
    }

    try {
      const base64Audio = await this.blobToBase64(audioBlob);

      // Extract format from mimeType (e.g., "audio/wav" -> "wav")
      const mimeType = audioBlob.type || 'audio/wav';
      const format = mimeType.split('/')[1]?.split(';')[0] || 'wav';

      const response = await apiClient.post<ChunkResponse>('/transcription/realtime/chunk', {
        jobId: this.jobId,
        audioData: base64Audio,
        chunkNumber: this.chunkNumber++,
        format: format
      });

      console.log('Chunk sent:', response.data.chunkId);
      return response.data;
    } catch (error: any) {
      console.error('Error sending chunk:', error);
      throw new Error(error.response?.data?.error || 'Failed to send audio chunk');
    }
  }

  /**
   * End the transcription session and get the full transcript
   * POST /transcription/realtime/end
   */
  async endSession(): Promise<EndSessionResponse> {
    if (!this.jobId) {
      throw new Error('No active session to end.');
    }

    try {
      const response = await apiClient.post<EndSessionResponse>('/transcription/realtime/end', {
        jobId: this.jobId
      });

      console.log('Session ended. Full transcript received.');

      // Clean up
      const result = response.data;
      this.jobId = null;

      return result;
    } catch (error: any) {
      console.error('Error ending session:', error);
      this.jobId = null;
      throw new Error(error.response?.data?.error || 'Failed to end transcription session');
    }
  }

  /**
   * Connect to Pusher to receive chunk.processed events
   */
  connectPusher(
    onChunkProcessed: (event: ChunkProcessedEvent) => void,
    onError?: (error: string) => void,
    onConnectionChange?: (connected: boolean) => void
  ): void {
    if (!this.jobId) {
      console.error('No jobId available for Pusher subscription');
      return;
    }

    this.onChunkProcessed = onChunkProcessed;
    this.onError = onError || null;
    this.onConnectionChange = onConnectionChange || null;

    const user = getCurrentUser();
    const token = user?.token;

    if (!token) {
      console.error('No auth token available for Pusher');
      this.onError?.('Authentication required for real-time updates');
      return;
    }

    const pusherKey = process.env.REACT_APP_PUSHER_APP_KEY;
    const pusherCluster = process.env.REACT_APP_PUSHER_CLUSTER || 'eu';
    const apiUrl = process.env.REACT_APP_API_URL_DEV || process.env.REACT_APP_API_URL_PROD || '';

    if (!pusherKey) {
      console.error('Pusher APP_KEY not configured');
      this.onError?.('Pusher not configured');
      return;
    }

    try {
      // Initialize Pusher
      this.pusher = new Pusher(pusherKey, {
        cluster: pusherCluster,
        authEndpoint: `${apiUrl}/broadcasting/auth`,
        auth: {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      });

      // Subscribe to private channel for this job
      const channelName = `private-transcription.${this.jobId}`;
      console.log('Subscribing to Pusher channel:', channelName);

      this.channel = this.pusher.subscribe(channelName);

      // Bind to chunk.processed event
      this.channel.bind('chunk.processed', (data: any) => {
        console.log('Pusher chunk.processed event:', data);

        if (this.onChunkProcessed) {
          this.onChunkProcessed({
            type: 'chunk.processed',
            chunkId: data.chunkId || `chunk_${data.chunkNumber}`,
            text: data.text || '',
            isFinal: true, // Pusher events are always final
            timestamp: Date.now(),
            chunkNumber: data.chunkNumber,
            duration: data.duration,
            success: data.success,
            error: data.error
          });
        }

        // Handle errors from backend
        if (data.error) {
          this.onError?.(data.error);
        }
      });

      // Handle connection state changes
      this.pusher.connection.bind('connected', () => {
        console.log('Pusher connected');
        this.onConnectionChange?.(true);
      });

      this.pusher.connection.bind('disconnected', () => {
        console.log('Pusher disconnected');
        this.onConnectionChange?.(false);
      });

      this.pusher.connection.bind('error', (err: any) => {
        console.error('Pusher connection error:', err);
        this.onError?.('Connection error');
      });

      // Handle subscription errors
      this.channel.bind('pusher:subscription_error', (err: any) => {
        console.error('Pusher subscription error:', err);
        this.onError?.('Failed to subscribe to channel');
      });

      this.channel.bind('pusher:subscription_succeeded', () => {
        console.log('Pusher subscription succeeded');
      });

    } catch (error) {
      console.error('Error initializing Pusher:', error);
      this.onError?.('Failed to initialize real-time connection');
    }
  }

  /**
   * Disconnect from Pusher
   */
  disconnectPusher(): void {
    if (this.channel && this.jobId) {
      const channelName = `private-transcription.${this.jobId}`;
      this.pusher?.unsubscribe(channelName);
      this.channel = null;
    }

    if (this.pusher) {
      this.pusher.disconnect();
      this.pusher = null;
    }

    this.onChunkProcessed = null;
    this.onError = null;
    this.onConnectionChange = null;
    console.log('Pusher disconnected');
  }

  // Legacy method names for compatibility
  connectWebSocket = this.connectPusher.bind(this);
  disconnectWebSocket = this.disconnectPusher.bind(this);

  /**
   * Get current job ID
   */
  getJobId(): string | null {
    return this.jobId;
  }

  /**
   * Check if Pusher is connected
   */
  isConnected(): boolean {
    return this.pusher !== null && this.pusher.connection.state === 'connected';
  }

  // Private methods
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          // Remove data URL prefix (e.g., "data:audio/wav;base64,")
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}

// Export singleton instance
export const whisperTranscriptionService = new WhisperTranscriptionService();

// Export class for testing
export default WhisperTranscriptionService;
